/* ==========================================================================
   Ryutronic theme — front-end behaviour
   Vanilla JS, no dependencies.
   ========================================================================== */

(function () {
  'use strict';

  var config = window.Ryutronic || {};
  var routes = config.routes || {};

  /* ----------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(eventName, selector, handler) {
    document.addEventListener(eventName, function (event) {
      var target = event.target.closest(selector);
      if (target) handler(event, target);
    });
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'Request failed');
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  function fetchText(url) {
    return fetch(url).then(function (response) {
      if (!response.ok) throw new Error('Request failed: ' + response.status);
      return response.text();
    });
  }

  /* ----------------------------------------------------------------------
     Overlay / drawer manager
     ---------------------------------------------------------------------- */

  var Overlay = {
    active: null,

    backdrop: function () {
      return $('[data-overlay-backdrop]');
    },

    open: function (element, openClass) {
      if (!element) return;
      this.close();
      this.active = { element: element, openClass: openClass || 'is-open' };

      element.classList.add(this.active.openClass);
      element.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');

      var backdrop = this.backdrop();
      if (backdrop) {
        backdrop.hidden = false;
        requestAnimationFrame(function () {
          backdrop.classList.add('is-visible');
        });
      }

      var focusable = element.querySelector('input, button, a[href]');
      if (focusable) {
        setTimeout(function () {
          focusable.focus();
        }, 320);
      }
    },

    close: function () {
      if (!this.active) return;
      var element = this.active.element;
      element.classList.remove(this.active.openClass);
      element.setAttribute('aria-hidden', 'true');
      this.active = null;

      document.body.classList.remove('overflow-hidden');

      var backdrop = this.backdrop();
      if (backdrop) {
        backdrop.classList.remove('is-visible');
        setTimeout(function () {
          if (!Overlay.active) backdrop.hidden = true;
        }, 300);
      }
    },

    isOpen: function (element) {
      return !!(this.active && this.active.element === element);
    }
  };

  document.addEventListener('click', function (event) {
    if (event.target.matches('[data-overlay-backdrop]')) Overlay.close();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') Overlay.close();
  });

  on('click', '[data-drawer-close], [data-menu-close], [data-search-close]', function (event) {
    event.preventDefault();
    Overlay.close();
  });

  /* ----------------------------------------------------------------------
     Mobile menu + search modal
     ---------------------------------------------------------------------- */

  on('click', '[data-menu-open]', function (event) {
    event.preventDefault();
    Overlay.open($('[data-menu-drawer]'));
  });

  on('click', '[data-submenu-toggle]', function (event, trigger) {
    event.preventDefault();
    var list = trigger.nextElementSibling;
    if (!list) return;
    var isOpen = list.classList.toggle('is-open');
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });

  on('click', '[data-search-open]', function (event) {
    event.preventDefault();
    Overlay.open($('[data-search-modal]'));
  });

  /* ----------------------------------------------------------------------
     Predictive search
     ---------------------------------------------------------------------- */

  (function initPredictiveSearch() {
    var input = $('[data-search-input]');
    var results = $('[data-predictive-results]');
    if (!input || !results || !routes.predictiveSearch) return;

    var timer = null;
    var lastQuery = '';

    input.addEventListener('input', function () {
      var query = input.value.trim();
      clearTimeout(timer);

      if (query.length < 2) {
        results.innerHTML = '';
        lastQuery = '';
        return;
      }

      timer = setTimeout(function () {
        if (query === lastQuery) return;
        lastQuery = query;

        var url =
          routes.predictiveSearch +
          '?q=' +
          encodeURIComponent(query) +
          '&resources[type]=product&resources[limit]=8&section_id=predictive-search';

        fetchText(url)
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var section = parsed.querySelector('#shopify-section-predictive-search');
            results.innerHTML = section ? section.innerHTML : '';
          })
          .catch(function () {
            results.innerHTML = '';
          });
      }, 250);
    });
  })();

  /* ----------------------------------------------------------------------
     Cart
     ---------------------------------------------------------------------- */

  var Cart = {
    drawer: function () {
      return $('[data-cart-drawer]');
    },

    openDrawer: function () {
      var drawer = this.drawer();
      if (drawer) Overlay.open(drawer);
    },

    updateCount: function (count) {
      $$('[data-cart-count]').forEach(function (element) {
        element.textContent = count;
        element.hidden = count === 0;
      });
      $$('[data-cart-item-count]').forEach(function (element) {
        element.textContent = '(' + count + ')';
      });
    },

    refreshDrawer: function () {
      var container = document.getElementById('shopify-section-cart-drawer');
      if (!container) return Promise.resolve();

      var wasOpen = Overlay.isOpen(this.drawer());

      return fetchText((routes.root || '/') + '?sections=cart-drawer')
        .then(function (text) {
          var data = JSON.parse(text);
          if (!data['cart-drawer']) return;

          var parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
          var fresh = parsed.querySelector('#shopify-section-cart-drawer');
          container.innerHTML = fresh ? fresh.innerHTML : data['cart-drawer'];

          if (wasOpen) {
            var drawer = Cart.drawer();
            if (drawer) {
              drawer.classList.add('is-open');
              drawer.setAttribute('aria-hidden', 'false');
              Overlay.active = { element: drawer, openClass: 'is-open' };
            }
          }
        })
        .catch(function () {
          /* leave the existing markup in place */
        });
    },

    add: function (formData, button) {
      var self = this;
      if (button && button.classList.contains('is-loading')) return Promise.resolve();
      var form = button ? button.closest('form') : null;
      var previousError = form ? form.querySelector('[data-form-error]') : null;
      if (previousError) {
        previousError.hidden = true;
        previousError.textContent = '';
      }
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');
      }

      return fetchJSON((routes.cartAdd || '/cart/add') + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function () {
          return self.getState();
        })
        .then(function (cart) {
          self.updateCount(cart.item_count);
          return self.refreshDrawer().then(function () {
            if (config.cartType === 'drawer') {
              self.openDrawer();
            } else {
              window.location.href = routes.cart;
            }
          });
        })
        .catch(function (error) {
          self.showError(button, error);
        })
        .finally(function () {
          if (button) {
            button.removeAttribute('aria-disabled');
            button.classList.remove('is-loading');
          }
        });
    },

    change: function (key, quantity) {
      var self = this;
      return fetchJSON((routes.cartChange || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (cart) {
          self.updateCount(cart.item_count);

          if (document.body.classList.contains('template-cart')) {
            window.location.reload();
            return cart;
          }

          return self.refreshDrawer().then(function () {
            return cart;
          });
        })
        .catch(function () {
          window.location.reload();
        });
    },

    getState: function () {
      return fetchJSON((routes.cart || '/cart') + '.js');
    },

    showError: function (button, error) {
      var message = (error && error.data && error.data.description) || (config.strings && config.strings.cartError);
      var target = button ? button.closest('form') : null;
      var errorEl = target ? target.querySelector('[data-form-error]') : null;

      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else if (message) {
        window.alert(message);
      }
    }
  };

  /* Cart icon opens the drawer instead of navigating */
  on('click', '[data-cart-toggle]', function (event) {
    if (config.cartType !== 'drawer') return;
    event.preventDefault();
    Cart.refreshDrawer().then(function () {
      Cart.openDrawer();
    });
  });

  /* AJAX add to cart */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches('form[data-product-form], form[action*="/cart/add"]')) return;

    event.preventDefault();
    var button = form.querySelector('[type="submit"]');
    if (button && button.disabled) return;
    var idInput = form.querySelector('[name="id"]');
    if (idInput && idInput.disabled) return;
    Cart.add(new FormData(form), button);
  });

  /* Quantity steppers in the cart */
  on('click', '[data-qty-change]', function (event, button) {
    event.preventDefault();
    var key = button.getAttribute('data-qty-change');
    var delta = parseInt(button.getAttribute('data-qty-delta'), 10);
    var row = button.closest('[data-cart-item]');
    var input = row ? row.querySelector('[data-qty-input]') : $('[data-qty-input="' + key + '"]');
    if (!input) return;

    var quantity = Math.max(0, parseInt(input.value, 10) + delta);
    input.value = quantity;
    Cart.change(key, quantity);
  });

  on('change', '[data-qty-input]', function (event, input) {
    var key = input.getAttribute('data-qty-input');
    var quantity = Math.max(0, parseInt(input.value, 10) || 0);
    Cart.change(key, quantity);
  });

  on('click', '[data-cart-remove]', function (event, button) {
    event.preventDefault();
    Cart.change(button.getAttribute('data-cart-remove'), 0);
  });

  /* Quantity stepper on the product page */
  on('click', '[data-product-qty-change]', function (event, button) {
    event.preventDefault();
    var form = button.closest('form');
    var input = form ? form.querySelector('[data-product-qty-input]') : null;
    if (!input) return;

    var delta = parseInt(button.getAttribute('data-product-qty-change'), 10);
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + delta);
  });

  /* ----------------------------------------------------------------------
     Variant picker
     ---------------------------------------------------------------------- */

  function initVariantSelects(root) {
    var container = root;
    if (!container || container.hasAttribute('data-variant-ready')) return;
    container.setAttribute('data-variant-ready', '');

    var dataScript = container.querySelector('[data-variant-data]');
    if (!dataScript) return;

    var variants;
    try {
      variants = JSON.parse(dataScript.textContent);
    } catch (error) {
      return;
    }

    var sectionId = container.getAttribute('data-section');
    var productUrl = container.getAttribute('data-url');

    function selectedOptions() {
      return $$('input[type="radio"]:checked', container).map(function (input) {
        return input.value;
      });
    }

    function findVariant(options) {
      return (
        variants.find(function (variant) {
          return variant.options.every(function (option, index) {
            return option === options[index];
          });
        }) || null
      );
    }

    function updateLabels(options) {
      $$('[data-option-value]', container).forEach(function (element, index) {
        if (options[index]) element.textContent = options[index];
      });
    }

    function updateAvailability(options) {
      $$('input[type="radio"]', container).forEach(function (input) {
        var position = parseInt(input.getAttribute('data-option-position'), 10) - 1;
        var candidate = options.slice();
        candidate[position] = input.value;

        var match = variants.find(function (variant) {
          return variant.options.every(function (option, index) {
            if (index === position) return option === input.value;
            if (!candidate[index]) return true;
            return option === candidate[index];
          });
        });

        var label = container.querySelector('label[for="' + input.id + '"]');
        if (!label) return;
        label.classList.toggle('is-unavailable', !match || !match.available);
      });
    }

    function renderSection(variant) {
      if (!sectionId || !productUrl) return;

      var url = productUrl + '?variant=' + variant.id + '&section_id=' + sectionId;

      fetchText(url)
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');

          var freshPrice = parsed.querySelector('[data-price-container]');
          var currentPrice = $('[data-price-container]');
          if (freshPrice && currentPrice) currentPrice.innerHTML = freshPrice.innerHTML;

          var freshInventory = parsed.querySelector('.inventory');
          var currentInventory = $('.inventory');
          if (freshInventory && currentInventory) {
            currentInventory.className = freshInventory.className;
            currentInventory.innerHTML = freshInventory.innerHTML;
          }

          var freshButton = parsed.querySelector('[data-add-to-cart]');
          var currentButton = $('[data-add-to-cart]');
          var freshInput = parsed.querySelector('[data-variant-id-input]');
          var currentInput = $('[data-variant-id-input]');
          if (freshInput && currentInput) currentInput.disabled = freshInput.hasAttribute('disabled');

          if (freshButton && currentButton) {
            currentButton.disabled = freshButton.hasAttribute('disabled');
            currentButton.innerHTML = freshButton.innerHTML;
          }
        })
        .catch(function () {
          /* keep the current values */
        });
    }

    function scrollToMedia(variant) {
      if (!variant.featured_media) return;
      var media = $('[data-media-id="' + variant.featured_media.id + '"]');
      if (!media) return;

      var gallery = media.closest('[data-product-gallery]');
      if (gallery && window.innerWidth >= 990) {
        gallery.prepend(media);
      } else {
        media.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    container.addEventListener('change', function () {
      var options = selectedOptions();
      updateLabels(options);
      updateAvailability(options);

      var variant = findVariant(options);
      var input = $('[data-variant-id-input]');
      var button = $('[data-add-to-cart]');

      if (!variant) {
        if (input) input.disabled = true;
        if (button) {
          button.disabled = true;
          button.textContent = (config.strings && config.strings.unavailable) || 'Unavailable';
        }
        return;
      }

      if (input) {
        input.disabled = false;
        input.value = variant.id;
      }

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      renderSection(variant);
      scrollToMedia(variant);
    });

    updateAvailability(selectedOptions());
  }

  /* ----------------------------------------------------------------------
     Accordions
     ---------------------------------------------------------------------- */

  on('click', '[data-accordion-trigger]', function (event, trigger) {
    event.preventDefault();
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;

    var isOpen = trigger.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      panel.style.height = panel.scrollHeight + 'px';
      requestAnimationFrame(function () {
        panel.style.height = '0px';
      });
      trigger.setAttribute('aria-expanded', 'false');
    } else {
      panel.style.height = panel.scrollHeight + 'px';
      trigger.setAttribute('aria-expanded', 'true');
      panel.addEventListener('transitionend', function handler() {
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', handler);
      });
    }
  });

  /* ----------------------------------------------------------------------
     Tabs
     ---------------------------------------------------------------------- */

  function initTabs(root) {
    $$('[data-tabs-root]', root).forEach(function (container) {
      var tabs = $$('[data-tab]', container);
      var panels = $$('[data-tab-panel]', container);

      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (item) {
            item.setAttribute('aria-selected', 'false');
          });
          panels.forEach(function (panel) {
            panel.hidden = true;
          });

          tab.setAttribute('aria-selected', 'true');
          if (panels[index]) panels[index].hidden = false;
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
     Horizontal scrollers
     ---------------------------------------------------------------------- */

  function initScrollers(root) {
    $$('[data-scroller]', root).forEach(function (scroller) {
      var track = $('[data-scroller-track]', scroller);
      var prev = $('[data-scroller-prev]', scroller);
      var next = $('[data-scroller-next]', scroller);
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        return first ? first.getBoundingClientRect().width + 24 : track.clientWidth * 0.8;
      }

      function sync() {
        if (!prev || !next) return;
        var maxScroll = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= maxScroll;

        var hidden = track.scrollWidth <= track.clientWidth + 2;
        prev.style.visibility = hidden ? 'hidden' : '';
        next.style.visibility = hidden ? 'hidden' : '';
      }

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          track.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ----------------------------------------------------------------------
     Header behaviour
     ---------------------------------------------------------------------- */

  (function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    var isTransparent = header.getAttribute('data-transparent') === 'true';
    var isSticky = header.getAttribute('data-sticky') === 'true';
    if (!isSticky) return;

    if (!isTransparent) {
      var lastState = false;
      window.addEventListener(
        'scroll',
        function () {
          var stuck = window.scrollY > 4;
          if (stuck !== lastState) {
            header.classList.toggle('is-stuck', stuck);
            lastState = stuck;
          }
        },
        { passive: true }
      );
      return;
    }

    var threshold = function () {
      return Math.max(window.innerHeight * 0.5, 200);
    };

    var stuckState = false;
    window.addEventListener(
      'scroll',
      function () {
        var stuck = window.scrollY > threshold();
        if (stuck !== stuckState) {
          header.classList.toggle('is-stuck', stuck);
          stuckState = stuck;
        }
      },
      { passive: true }
    );
  })();

  /* ----------------------------------------------------------------------
     Reveal on scroll
     ---------------------------------------------------------------------- */

  function initReveal(root) {
    var elements = $$('.reveal:not(.is-visible)', root);
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (element) {
        element.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );

    elements.forEach(function (element) {
      observer.observe(element);
    });
  }

  /* ----------------------------------------------------------------------
     Localization + facets
     ---------------------------------------------------------------------- */

  on('change', 'select[name="country_code"], select[name="locale_code"]', function (event, select) {
    var form = select.closest('form');
    if (form) form.submit();
  });

  on('change', '[data-facet-select]', function () {
    var form = document.getElementById('FacetForm');
    if (form) form.submit();
  });

  /* ----------------------------------------------------------------------
     Country / province selector
     ---------------------------------------------------------------------- */

  function initCountrySelectors(root) {
    $$('[data-address-country]', root).forEach(function (countrySelect) {
      var provinceSelect = document.getElementById(countrySelect.getAttribute('data-province-target'));
      var wrapper = document.getElementById(countrySelect.getAttribute('data-province-wrapper'));
      if (!provinceSelect || !wrapper) return;

      function update() {
        var option = countrySelect.options[countrySelect.selectedIndex];
        if (!option) return;

        var provinces = [];
        try {
          provinces = JSON.parse(option.getAttribute('data-provinces') || '[]');
        } catch (error) {
          provinces = [];
        }

        provinceSelect.innerHTML = '';

        if (!provinces.length) {
          wrapper.style.display = 'none';
          return;
        }

        provinces.forEach(function (province) {
          var opt = document.createElement('option');
          opt.value = province[0];
          opt.textContent = province[1];
          provinceSelect.appendChild(opt);
        });

        var defaultProvince = provinceSelect.getAttribute('data-default');
        if (defaultProvince) provinceSelect.value = defaultProvince;

        wrapper.style.display = '';
      }

      var defaultCountry = countrySelect.getAttribute('data-default');
      if (defaultCountry) countrySelect.value = defaultCountry;

      countrySelect.addEventListener('change', update);
      update();
    });
  }

  /* ----------------------------------------------------------------------
     Product recommendations
     ---------------------------------------------------------------------- */

  function initRecommendations(root) {
    $$('[data-product-recommendations]', root).forEach(function (container) {
      var url = container.getAttribute('data-url');
      if (!url) return;

      fetchText(url)
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-product-recommendations]');
          if (fresh && fresh.innerHTML.trim()) {
            container.innerHTML = fresh.innerHTML;
            initReveal(container);
          }
        })
        .catch(function () {
          /* recommendations are optional */
        });
    });
  }

  /* ----------------------------------------------------------------------
     Copy link
     ---------------------------------------------------------------------- */

  on('click', '[data-copy-link]', function (event, button) {
    event.preventDefault();
    var value = button.getAttribute('data-copy-link');
    var original = button.textContent;

    var done = function () {
      button.textContent = (config.strings && config.strings.copied) || 'Link copied';
      setTimeout(function () {
        button.textContent = original;
      }, 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(done).catch(function () {});
    }
  });

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function init(root) {
    initTabs(root);
    initScrollers(root);
    initReveal(root);
    initCountrySelectors(root);
    initRecommendations(root);
    $$('[data-variant-selects]', root).forEach(function (element) {
      initVariantSelects(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme editor support — re-run for newly rendered sections */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    if (event.target.querySelector('[data-cart-drawer]')) Cart.openDrawer();
  });

  window.RyutronicCart = Cart;
})();
